import React, { useEffect, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function PaymentEntry() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const defaultHolderId = user?.holder_id || "";
  const [parties, setParties] = useState([]);
  const [holders, setHolders] = useState([]);
  const [form, setForm] = useState({
    party_id: "", direction: "received", amount: 0,
    mode: "cash", reference: "", notes: "", date: todayISO(),
    received_by_holder_id: defaultHolderId, applies_to: "gas",
  });
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, r, h] = await Promise.all([
      api.get("/parties"), api.get("/payments"), api.get("/cash-holders"),
    ]);
    // managers only see customers for payment entry
    setParties(isManager ? p.data.filter((x) => x.type === "customer") : p.data);
    setRecent(r.data.slice(0, 20));
    setHolders(h.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party_id || Number(form.amount) <= 0) {
      toast.error("Party and amount required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.received_by_holder_id) payload.received_by_holder_id = null;
      await api.post("/payments", payload);
      toast.success("Payment saved");
      setForm({ ...form, amount: 0, reference: "", notes: "", received_by_holder_id: defaultHolderId });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const partyName = (id) => parties.find((p) => p.id === id)?.name || "—";
  const holderName = (id) => {
    const h = holders.find((x) => x.id === id);
    return h ? `${h.name}${h.location ? ` — ${h.location}` : ""}` : "—";
  };

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="payment-form">
        <div>
          <label className="small-label block mb-2">Date</label>
          <input type="date" className="input-lg" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        </div>
        <div>
          <label className="small-label block mb-2">Party</label>
          <select className="input-lg" value={form.party_id}
            onChange={(e) => setForm({ ...form, party_id: e.target.value })} required
            data-testid="payment-party">
            <option value="">— Select —</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.type})</option>)}
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Direction</label>
          <select className="input-lg" value={form.direction}
            onChange={(e) => setForm({ ...form, direction: e.target.value })}
            data-testid="payment-direction" disabled={isManager}>
            <option value="received">Received (from customer)</option>
            {!isManager && <option value="paid">Paid (to vendor)</option>}
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Mode</label>
          <select className="input-lg" value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value })}>
            <option>cash</option><option>upi</option><option>cheque</option><option>bank</option><option>other</option>
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Towards</label>
          <select className="input-lg" value={form.applies_to}
            onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
            data-testid="payment-applies-to">
            <option value="gas">Gas Ledger</option>
            <option value="materials">Materials Ledger</option>
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Amount ₹</label>
          <input type="number" step="0.01" className="input-lg" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} required
            data-testid="payment-amount" />
        </div>
        {form.direction === "received" && (
          <div>
            <label className="small-label block mb-2">Received by</label>
            <select className="input-lg" value={form.received_by_holder_id}
              onChange={(e) => setForm({ ...form, received_by_holder_id: e.target.value })}
              data-testid="payment-received-by"
              disabled={isManager && !!defaultHolderId}>
              <option value="">Owner / bank (default)</option>
              {holders.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}{h.location ? ` — ${h.location}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={form.direction === "received" ? "" : "lg:col-span-2"}>
          <label className="small-label block mb-2">Reference</label>
          <input className="input-lg" value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
            placeholder="Cheque no / UTR / Txn id" />
        </div>
        <div>
          <label className="small-label block mb-2">Notes</label>
          <input className="input-lg" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
          <button className="btn-primary flex items-center gap-2 h-11" disabled={busy} data-testid="payment-submit">
            <Save size={16} /> {busy ? "Saving…" : "Save Payment"}
          </button>
        </div>
      </form>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500">Recent Payments</div>
        <div className="table-wrap">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th><th className="text-left">Party</th>
              <th className="text-left">Direction</th><th className="text-left">Mode</th>
              <th className="text-left">Towards</th>
              <th className="text-left">Received by</th>
              <th className="num">Amount</th><th className="text-left">Reference</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((p) => (
              <tr key={p.id}>
                <td className="data-num text-xs">{p.date}</td>
                <td>{partyName(p.party_id)}</td>
                <td className="capitalize">{p.direction}</td>
                <td className="capitalize">{p.mode}</td>
                <td>
                  <span className={`px-2 py-0.5 text-[10px] rounded-sm font-bold uppercase tracking-wide ${p.applies_to === "materials" ? "bg-[#FEF3C7] text-[#B45309]" : "bg-gray-100 text-gray-600"}`}>
                    {p.applies_to === "materials" ? "Materials" : "Gas"}
                  </span>
                </td>
                <td className="text-xs">{p.received_by_holder_id ? holderName(p.received_by_holder_id) : "—"}</td>
                <td className="num">₹ {formatMoney(p.amount)}</td>
                <td className="text-xs text-gray-600">{p.reference || "—"}</td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td colSpan="8" className="py-4 text-center text-gray-400 text-sm">No payments yet</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
